/**
 * useMotionDetection Hook
 *
 * React hook for integrating motion detection into Baby Mode.
 * Handles initialization, lifecycle, and state management.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  MotionDetector,
  MotionDetectionResult,
  MotionDetectionStatus,
} from "@/lib/motionDetection";
import type { MotionDetectionConfig } from "@/lib/motionDetection";
import {
  detectDeviceCapability,
  DeviceCapability,
  shouldEnableMotionDetection,
} from "@/lib/deviceCapability";
import {
  STORAGE_KEYS,
  MOTION_SENSITIVITY_LEVELS,
  DEFAULT_SENSITIVITY_LEVEL,
  type MotionSensitivityLevel,
} from "@/constants";

export type MotionDetectionPreference = "auto" | "on" | "off";

interface UseMotionDetectionOptions {
  // User preference for motion detection
  preference?: MotionDetectionPreference;

  // Custom configuration overrides
  config?: Partial<MotionDetectionConfig>;

  // Callbacks
  onMotionDetected?: (result: MotionDetectionResult) => void;
  onError?: (error: Error) => void;
}

interface UseMotionDetectionReturn {
  // Status
  status: MotionDetectionStatus;
  isEnabled: boolean;
  isRunning: boolean;

  // Device capability info
  deviceCapability: DeviceCapability | null;
  isCapabilityLoading: boolean;

  // Latest detection result (for visualization)
  lastResult: MotionDetectionResult | null;

  // Stats
  motionCount: number;

  // Current motion level (0-100 for visualization)
  motionLevel: number;

  // Controls
  enable: () => Promise<void>;
  disable: () => void;
  toggleEnabled: () => Promise<void>;

  // Preference management
  setPreference: (pref: MotionDetectionPreference) => void;
  preference: MotionDetectionPreference;

  // Sensitivity level
  sensitivityLevel: MotionSensitivityLevel;
  setSensitivityLevel: (level: MotionSensitivityLevel) => void;

  // Error state
  error: string | null;
}

/**
 * Load preference from localStorage - defaults to 'off'
 */
function loadPreference(): MotionDetectionPreference {
  if (typeof window === "undefined") return "off";

  const stored = localStorage.getItem(STORAGE_KEYS.motionDetectionEnabled);
  if (stored === "on" || stored === "off" || stored === "auto") {
    return stored;
  }
  return "off"; // Default to OFF
}

/**
 * Save preference to localStorage
 */
function savePreference(pref: MotionDetectionPreference): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEYS.motionDetectionEnabled, pref);
}

/**
 * Load sensitivity level from localStorage
 */
function loadSensitivityLevel(): MotionSensitivityLevel {
  if (typeof window === "undefined") return DEFAULT_SENSITIVITY_LEVEL;

  const stored = localStorage.getItem(STORAGE_KEYS.motionSensitivityLevel);
  if (stored === "whisper" || stored === "aware" || stored === "vigilant") {
    return stored;
  }
  return DEFAULT_SENSITIVITY_LEVEL;
}

/**
 * Save sensitivity level to localStorage
 */
function saveSensitivityLevel(level: MotionSensitivityLevel): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEYS.motionSensitivityLevel, level);
}

/**
 * useMotionDetection Hook
 */
export function useMotionDetection(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  isVideoEnabled: boolean,
  options: UseMotionDetectionOptions = {},
): UseMotionDetectionReturn {
  const { config: configOverrides, onMotionDetected, onError } = options;

  // State
  const [status, setStatus] = useState<MotionDetectionStatus>("idle");
  const [deviceCapability, setDeviceCapability] =
    useState<DeviceCapability | null>(null);
  const [isCapabilityLoading, setIsCapabilityLoading] = useState(true);
  const [lastResult, setLastResult] = useState<MotionDetectionResult | null>(
    null,
  );
  const [motionCount, setMotionCount] = useState(0);
  const [motionLevel, setMotionLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [preference, setPreferenceState] = useState<MotionDetectionPreference>(
    () => options.preference ?? loadPreference(),
  );
  const [isEnabled, setIsEnabled] = useState(false);
  const [sensitivityLevel, setSensitivityLevelState] =
    useState<MotionSensitivityLevel>(() => loadSensitivityLevel());

  // Refs
  const detectorRef = useRef<MotionDetector | null>(null);
  const isInitializingRef = useRef(false);

  // Derived state
  const isRunning = status === "running";

  /**
   * Handle preference change
   */
  const setPreference = useCallback(
    (pref: MotionDetectionPreference) => {
      setPreferenceState(pref);
      savePreference(pref);

      // Update enabled state based on new preference
      if (deviceCapability) {
        const shouldEnable = shouldEnableMotionDetection(
          deviceCapability,
          pref,
        );
        if (!shouldEnable && isEnabled) {
          // Disable immediately
          detectorRef.current?.stop();
          setIsEnabled(false);
        }
      }
    },
    [deviceCapability, isEnabled],
  );

  /**
   * Handle sensitivity level change
   */
  const setSensitivityLevel = useCallback((level: MotionSensitivityLevel) => {
    setSensitivityLevelState(level);
    saveSensitivityLevel(level);
    console.log(`[useMotionDetection] Sensitivity level changed to: ${level}`);
  }, []);

  /**
   * Initialize detector
   */
  const initializeDetector = useCallback(
    async (videoElement: HTMLVideoElement, capability: DeviceCapability) => {
      if (isInitializingRef.current) return;
      isInitializingRef.current = true;

      try {
        // Clean up existing detector
        if (detectorRef.current) {
          detectorRef.current.stop();
          detectorRef.current = null;
        }

        // Create configuration based on device capability and sensitivity level
        const sensitivityConfig = MOTION_SENSITIVITY_LEVELS[sensitivityLevel];
        const config: Partial<MotionDetectionConfig> = {
          ...configOverrides,
          processingIntervalMs: capability.motionProcessingIntervalMs,
          motionThreshold: sensitivityConfig.threshold,
        };

        // Create detector with callbacks
        const detector = new MotionDetector(config, {
          onMotionDetected: (result) => {
            setMotionCount((prev) => prev + 1);
            onMotionDetected?.(result);
          },
          onAnalysis: (result) => {
            setLastResult(result);
            setMotionLevel(result.motionLevel);
          },
          onStatusChange: (newStatus) => {
            setStatus(newStatus);
          },
          onError: (err) => {
            setError(err.message);
            onError?.(err);
          },
        });

        // Initialize with video element
        await detector.initialize(videoElement);

        detectorRef.current = detector;
        setIsEnabled(true);
        setError(null);

        console.log("[useMotionDetection] Detector initialized");
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to initialize motion detection";
        setError(errorMessage);
        setIsEnabled(false);
        console.error("[useMotionDetection] Initialization error:", err);
      } finally {
        isInitializingRef.current = false;
      }
    },
    [configOverrides, onMotionDetected, onError, sensitivityLevel],
  );

  /**
   * Enable motion detection
   */
  const enable = useCallback(async () => {
    const videoElement = videoRef.current;
    if (!videoElement || !deviceCapability) {
      setError(
        "Cannot enable: no video element or device capability not detected",
      );
      return;
    }

    if (!isVideoEnabled) {
      setError("Cannot enable motion detection: video is not enabled");
      return;
    }

    await initializeDetector(videoElement, deviceCapability);

    if (detectorRef.current && detectorRef.current.getStatus() !== "error") {
      detectorRef.current.start();
    }
  }, [videoRef, deviceCapability, isVideoEnabled, initializeDetector]);

  /**
   * Disable motion detection
   */
  const disable = useCallback(() => {
    if (detectorRef.current) {
      detectorRef.current.stop();
    }
    setIsEnabled(false);
    setStatus("idle");
    setMotionLevel(0);
    console.log("[useMotionDetection] Disabled");
  }, []);

  /**
   * Toggle enabled state - updates preference to persist user's choice
   */
  const toggleEnabled = useCallback(async () => {
    if (isEnabled) {
      // User is turning OFF - set preference to 'off'
      setPreference("off");
      disable();
    } else {
      // User is turning ON - set preference to 'on'
      setPreference("on");
      await enable();
    }
  }, [isEnabled, enable, disable, setPreference]);

  /**
   * Detect device capability on mount
   */
  useEffect(() => {
    let mounted = true;

    async function detect() {
      try {
        setIsCapabilityLoading(true);
        const capability = await detectDeviceCapability();

        if (mounted) {
          setDeviceCapability(capability);
          setIsCapabilityLoading(false);
          console.log(
            "[useMotionDetection] Device capability detected:",
            capability,
          );
        }
      } catch (err) {
        if (mounted) {
          setIsCapabilityLoading(false);
          console.error(
            "[useMotionDetection] Failed to detect device capability:",
            err,
          );
        }
      }
    }

    detect();

    return () => {
      mounted = false;
    };
  }, []);

  /**
   * Auto-enable based on preference when video and capability are ready
   */
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !deviceCapability || isCapabilityLoading) {
      return;
    }

    // Motion detection requires video to be enabled
    if (!isVideoEnabled) {
      if (isEnabled) {
        disable();
      }
      return;
    }

    const shouldEnable = shouldEnableMotionDetection(
      deviceCapability,
      preference,
    );

    if (shouldEnable && !isEnabled && !isInitializingRef.current) {
      initializeDetector(videoElement, deviceCapability).then(() => {
        if (
          detectorRef.current &&
          detectorRef.current.getStatus() !== "error"
        ) {
          detectorRef.current.start();
        }
      });
    } else if (!shouldEnable && isEnabled) {
      disable();
    }
  }, [
    videoRef,
    deviceCapability,
    isCapabilityLoading,
    preference,
    isEnabled,
    isVideoEnabled,
    initializeDetector,
    disable,
  ]);

  /**
   * Handle video enable/disable changes
   */
  useEffect(() => {
    // If video is disabled while motion detection is running, stop it
    if (!isVideoEnabled && isEnabled) {
      console.log(
        "[useMotionDetection] Video disabled, stopping motion detection",
      );
      disable();
    }
  }, [isVideoEnabled, isEnabled, disable]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (detectorRef.current) {
        detectorRef.current.stop();
        detectorRef.current = null;
      }
    };
  }, []);

  return {
    // Status
    status,
    isEnabled,
    isRunning,

    // Device capability
    deviceCapability,
    isCapabilityLoading,

    // Detection
    lastResult,
    motionCount,
    motionLevel,

    // Controls
    enable,
    disable,
    toggleEnabled,

    // Preference
    setPreference,
    preference,

    // Sensitivity
    sensitivityLevel,
    setSensitivityLevel,

    // Error
    error,
  };
}
